/**
 * Three-layer parallax plus the environment's ambient weather.
 *
 * Layers are camera-fixed and offset by the camera's scroll, which keeps the
 * cost flat no matter how tall the tower is. Every layer comes from an asset key,
 * so Prompt 10's SVGs drop in through the manifest alone.
 *
 * Everything here respects reduced motion (CLAUDE.md): with it on, the parallax
 * still draws but nothing drifts, shimmers or falls.
 */
import Phaser from 'phaser';
import {
  ensureAuroraTexture,
  ensureEnvironmentTextures,
  AURORA_RIBBON_KEY,
  farLayerHeightFor,
  farLayerYFor,
  NEAR_TILE_HEIGHT,
} from '../../art/environments';
import { backgroundLayerKey, LAYER_SCROLL, paletteFor, themeBandFor } from '../../data/themes';
import type { AmbientEffect } from '../../data/themes';
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT } from '../../types';
import type { BackgroundTheme } from '../../types';

const SKY_DEPTH = -30;
const FAR_DEPTH = -20;
const NEAR_DEPTH = -10;
const AMBIENT_DEPTH = 60;

const SNOW_TEXTURE = 'fx/snowflake';
const STREAK_TEXTURE = 'fx/streak';

/**
 * How far the horizon layers slide down over a full climb. They settle rather
 * than scrolling away, so the frame stays anchored near the summit.
 */
const FAR_MAX_DROP = 240;
const NEAR_MAX_DROP = 180;

export class Background {
  private readonly sky: Phaser.GameObjects.Image;
  private readonly far: Phaser.GameObjects.Image;
  private readonly near: Phaser.GameObjects.Image;
  private readonly ribbons: Phaser.GameObjects.Image[] = [];
  private readonly emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];

  private readonly farBaseY: number;
  private readonly nearBaseY: number;
  private readonly skyPanRange: number;
  /** First scroll position seen, so the climb is measured relative to it. */
  private baseScrollY: number | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly env: BackgroundTheme,
    private readonly reducedMotion: boolean,
    ambient: AmbientEffect = 'none',
    private readonly climbHeight = 1,
  ) {
    ensureEnvironmentTextures(scene, env);

    this.sky = scene.add
      .image(0, 0, backgroundLayerKey(env, 'sky'))
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(SKY_DEPTH);
    // Generated oversized, so no scaling and no distorted sun. The tall stage
    // is the exception: the sky art is made for the wide one, so it is scaled
    // evenly (never stretched) to cover, and centred, cropping the sides.
    if (IS_PORTRAIT) {
      const scale = Math.max(GAME_WIDTH / this.sky.width, (GAME_HEIGHT * 1.35) / this.sky.height);
      this.sky.setScale(scale).setX((GAME_WIDTH - this.sky.width * scale) / 2);
    }
    this.skyPanRange = Math.max(0, this.sky.displayHeight - GAME_HEIGHT);
    // Start showing the bottom of the gradient: the horizon.
    this.sky.setY(-this.skyPanRange);

    // Plain images, not TileSprites: the strips never scroll sideways, and a
    // TileSprite repeats vertically too, so once the stage was drawn at the
    // screen's own resolution its top edge picked up a line of its bottom row.
    this.far = this.strip(
      scene,
      backgroundLayerKey(env, 'far'),
      farLayerYFor(env),
      farLayerHeightFor(env),
      FAR_DEPTH,
    );
    this.farBaseY = this.far.y;

    this.near = this.strip(
      scene,
      backgroundLayerKey(env, 'near'),
      GAME_HEIGHT - NEAR_TILE_HEIGHT,
      NEAR_TILE_HEIGHT,
      NEAR_DEPTH,
    );
    this.nearBaseY = this.near.y;

    this.addAmbient(ambient);
  }

  /** Builds the ambient effect for a specific band. */
  static ambientFor(level: number): AmbientEffect {
    return themeBandFor(level).ambient;
  }

  /** One background strip, its authored height, pinned to the camera. */
  private strip(
    scene: Phaser.Scene,
    key: string,
    y: number,
    height: number,
    depth: number,
  ): Phaser.GameObjects.Image {
    const image = scene.add.image(0, y, key).setOrigin(0, 0).setScrollFactor(0).setDepth(depth);
    // Whatever resolution the art loaded at, show it at its authored size.
    return image.setDisplaySize(image.width * (height / image.height), height);
  }

  /**
   * Called every frame. Layers are moved, never tile-scrolled vertically: a
   * TileSprite wraps, which made the ridge and the drifts repeat down the screen
   * once the player was a few platforms up.
   */
  update(): void {
    const scrollY = this.scene.cameras.main.scrollY;
    if (this.baseScrollY === null) this.baseScrollY = scrollY;

    // Pixels climbed since the level started. Scroll decreases going up.
    const climbed = Math.max(0, this.baseScrollY - scrollY);
    const progress = Math.min(1, climbed / Math.max(1, this.climbHeight));

    // Pan up the gradient as the tower is climbed, from horizon to deep sky.
    this.sky.setY(-this.skyPanRange * (1 - progress));

    // The horizon drifts down and then settles, so it never wraps or empties.
    this.far.setY(this.farBaseY + Math.min(FAR_MAX_DROP, climbed * LAYER_SCROLL.far));
    this.near.setY(this.nearBaseY + Math.min(NEAR_MAX_DROP, climbed * LAYER_SCROLL.near));

    for (const ribbon of this.ribbons) {
      const baseY: unknown = ribbon.getData('baseY');
      const start = typeof baseY === 'number' ? baseY : ribbon.y;
      ribbon.setY(start + Math.min(90, climbed * LAYER_SCROLL.sky));
    }
  }

  setAmbient(effect: AmbientEffect): void {
    this.clearAmbient();
    this.addAmbient(effect);
  }

  destroy(): void {
    this.clearAmbient();
    this.sky.destroy();
    this.far.destroy();
    this.near.destroy();
  }

  /* --------------------------------------------------------------- ambient */

  private addAmbient(effect: AmbientEffect): void {
    if (effect === 'none') return;
    const palette = paletteFor(this.env);

    if (effect === 'aurora') {
      this.addAurora();
      return;
    }

    // Reduced motion keeps the scene still: no falling snow, no streaks.
    if (this.reducedMotion) return;

    this.ensureParticleTextures();

    if (effect === 'snow') {
      this.emitters.push(
        this.scene.add
          .particles(0, -40, SNOW_TEXTURE, {
            x: { min: 0, max: GAME_WIDTH },
            speedY: { min: 24, max: 70 },
            speedX: { min: -22, max: 22 },
            lifespan: 9000,
            scale: { min: 0.35, max: 0.9 },
            alpha: { min: 0.35, max: 0.85 },
            quantity: 1,
            frequency: 180,
            tint: palette.ambientTint,
          })
          .setScrollFactor(0)
          .setDepth(AMBIENT_DEPTH),
      );
      return;
    }

    if (effect === 'blizzard') {
      this.emitters.push(
        this.scene.add
          .particles(GAME_WIDTH + 40, 0, STREAK_TEXTURE, {
            y: { min: -40, max: GAME_HEIGHT },
            speedX: { min: -900, max: -520 },
            speedY: { min: 40, max: 130 },
            lifespan: 2200,
            scaleX: { min: 1, max: 2.4 },
            scaleY: { min: 0.5, max: 1 },
            alpha: { min: 0.25, max: 0.7 },
            quantity: 2,
            frequency: 40,
            tint: palette.ambientTint,
          })
          .setScrollFactor(0)
          .setDepth(AMBIENT_DEPTH),
      );
      return;
    }

    if (effect === 'bubbles') {
      this.emitters.push(
        this.scene.add
          .particles(0, GAME_HEIGHT + 20, SNOW_TEXTURE, {
            x: { min: 0, max: GAME_WIDTH },
            speedY: { min: -90, max: -40 },
            lifespan: 7000,
            scale: { min: 0.25, max: 0.7 },
            alpha: { min: 0.2, max: 0.55 },
            quantity: 1,
            frequency: 320,
            tint: palette.ambientTint,
          })
          .setScrollFactor(0)
          .setDepth(AMBIENT_DEPTH),
      );
      return;
    }

    // dust: slow motes drifting in a shaft of cave light
    this.emitters.push(
      this.scene.add
        .particles(0, 0, SNOW_TEXTURE, {
          x: { min: 0, max: GAME_WIDTH },
          y: { min: 0, max: GAME_HEIGHT },
          speedY: { min: -12, max: 12 },
          speedX: { min: -14, max: 14 },
          lifespan: 6000,
          scale: { min: 0.18, max: 0.44 },
          alpha: { min: 0.15, max: 0.4 },
          quantity: 1,
          frequency: 400,
          tint: palette.ambientTint,
        })
        .setScrollFactor(0)
        .setDepth(AMBIENT_DEPTH),
    );
  }

  /** Slow shimmering curtains. Static when reduced motion is on. */
  private addAurora(): void {
    ensureAuroraTexture(this.scene);
    const palette = paletteFor(this.env);
    const tints = [palette.ambientTint, palette.nearAccent, palette.farAccent];

    for (let i = 0; i < 3; i += 1) {
      const baseY = 40 + i * 54;
      const ribbon = this.scene.add
        .image(GAME_WIDTH / 2, baseY, AURORA_RIBBON_KEY)
        .setOrigin(0.5, 0)
        .setScale(2.6, 1.5 + i * 0.3)
        .setAlpha(0.55)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setTint(tints[i] ?? palette.ambientTint)
        .setScrollFactor(0)
        .setDepth(SKY_DEPTH + 1);
      ribbon.setData('baseY', baseY);
      this.ribbons.push(ribbon);

      if (this.reducedMotion) continue;

      this.scene.tweens.add({
        targets: ribbon,
        alpha: { from: 0.32, to: 0.78 },
        scaleX: { from: 2.4, to: 2.9 },
        duration: 5200 + i * 1700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.scene.tweens.add({
        targets: ribbon,
        x: { from: GAME_WIDTH / 2 - 90, to: GAME_WIDTH / 2 + 90 },
        duration: 11000 + i * 2600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private clearAmbient(): void {
    for (const emitter of this.emitters) emitter.destroy();
    this.emitters.length = 0;
    for (const ribbon of this.ribbons) {
      this.scene.tweens.killTweensOf(ribbon);
      ribbon.destroy();
    }
    this.ribbons.length = 0;
  }

  private ensureParticleTextures(): void {
    if (!this.scene.textures.exists(SNOW_TEXTURE)) {
      const gfx = this.scene.make.graphics({ x: 0, y: 0 }, false);
      gfx.fillStyle(0xffffff, 1);
      gfx.fillCircle(6, 6, 6);
      gfx.generateTexture(SNOW_TEXTURE, 12, 12);
      gfx.destroy();
    }
    if (!this.scene.textures.exists(STREAK_TEXTURE)) {
      const gfx = this.scene.make.graphics({ x: 0, y: 0 }, false);
      gfx.fillStyle(0xffffff, 1);
      gfx.fillRoundedRect(0, 0, 44, 5, 2);
      gfx.generateTexture(STREAK_TEXTURE, 44, 5);
      gfx.destroy();
    }
  }
}
